import {
  redactPath,
  withUpstreamTiming,
} from "@/libs/diagnostics/upstream-timing";
import { ApiResponse } from "@/models/Response";

interface CachedResponse<T> {
  data: T;
  expiresAt: number;
}

export default class ApiResponseCache<T> {
  private static cacheRegistry: Map<string, ApiResponseCache<any>> = new Map();
  private cachedValue: CachedResponse<T> | null = null;
  private readonly ttlMs: number;
  private readonly endpoint: string;
  /** 타이밍 로그에 남길 엔드포인트 경로(쿼리 제거). 인스턴스당 한 번만 계산한다. */
  private readonly target: string;

  private constructor(endpoint: string, ttlMs: number = 10 * 60 * 1000) {
    this.endpoint = endpoint;
    this.ttlMs = ttlMs;
    this.target = ApiResponseCache.toTarget(endpoint);
  }

  private static toTarget(endpoint: string): string {
    try {
      return redactPath(new URL(endpoint).pathname);
    } catch {
      return redactPath(endpoint);
    }
  }

  /**
   * ⚠️ **`T` 는 응답 엔벨로프가 아니라 `message` 안쪽 모양이다.**
   *
   * `fetchFromEndpoint` 가 `const { message: data, status } = await response.json()` 로
   * 엔벨로프를 **여기서 벗겨** `data`(= `message`)를 돌려주기 때문이다. 소비처가 `T` 를
   * 엔벨로프로 선언하면 `.message` 를 한 번 더 벗기게 되고, 그 값은 항상 `undefined` 인데
   * **타입체크는 통과한다** — 기능만 조용히 죽는다(2026-09-14 #5755, `docs/pitfalls.md` §58
   * "래퍼가 이미 벗긴 응답을 한 번 더 벗기면").
   *
   * 올바른 예: `createInstance<FeatureFlag[]>` · `createInstance<Partial<WelcomeCouponOffer>>`
   *
   * ⚠️ `status && data` 를 통과 못 하면 **던진다** — `status:false`·`message:null`·평문 응답은
   * 값이 아니라 예외로 온다. `response.ok` 는 보지 않으므로 **비200 이어도 엔벨로프만
   * 멀쩡하면 성공**이다.
   */
  public static createInstance<T>(
    cacheKey: string,
    endpoint: string,
    ttlMs?: number,
  ): ApiResponseCache<T> {
    if (!this.cacheRegistry.has(cacheKey)) {
      this.cacheRegistry.set(
        cacheKey,
        new ApiResponseCache<T>(endpoint, ttlMs),
      );
    }
    return this.cacheRegistry.get(cacheKey) as ApiResponseCache<T>;
  }

  private isValid(): boolean {
    if (!this.cachedValue) return false;
    return Date.now() < this.cachedValue.expiresAt;
  }

  private async fetchFromEndpoint(): Promise<T> {
    try {
      const { message: data, status } = await withUpstreamTiming(
        "api-response-cache",
        async () => {
          const response = await fetch(this.endpoint);
          return (await response.json()) as ApiResponse<T>;
        },
        { meta: { target: this.target } },
      );

      if (status && data) {
        return data;
      }
    } catch (e) {
      console.error(`Failed to fetch from ${this.endpoint}:`, e);
    }
    throw new Error("Failed to fetch data");
  }

  public async getResponse(): Promise<T> {
    if (this.isValid() && this.cachedValue) {
      return this.cachedValue.data;
    }

    const data = await this.fetchFromEndpoint();
    this.cachedValue = {
      data,
      expiresAt: Date.now() + this.ttlMs,
    };

    return data;
  }

  public invalidate(): void {
    this.cachedValue = null;
  }
}
